import { useEffect, useMemo, useRef, useState } from "react";
import Button from "src/Components/Button";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { IDeltaSearch, IDeltaSimulationPosition } from "src/Interfaces/automaticMarketMakingDelta";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { deleteDeltaSimulation, getDeltaOptionChainTable, getDeltaPortfolioTable, getDeltaSummaryTable, resetDeltaSimulation, saveDeltaSimulation } from "src/Requests/automaticMarketMakingDelta";
import { getDueDates } from "src/Requests/optionGreek";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { OptionChainTableUI, PortfolioTableUI, SimulationActionsContext, SummaryTableUI } from "./DeltaTables";

type DeltaTableName = "summary" | "portfolio" | "chain";

// The report API accepts an Int32 page size; load all rows for the selected pair/expiry.
const ALL_ROWS_PAGE_SIZE = 2_147_483_647;

const DeltaPortfolio = ({ search }: { search: IDeltaSearch }) => {
  const [expiries, setExpiries] = useState<DropdownItem[]>();
  const [expiry, setExpiry] = useState<string>();
  const [simulationCount, setSimulationCount] = useState(0);
  const [hasMissingCalculations, setHasMissingCalculations] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [saving, setSaving] = useState(false);
  const mutationController = useRef(new AbortController());
  const pendingRefresh = useRef<{ key: number; remaining: Set<DeltaTableName> } | null>(null);
  const pair = useMemo(() => ({ accountId: search.accountId, baseInstrumentId: search.baseInstrumentId }), [search.accountId, search.baseInstrumentId]);
  const chainSearch = useMemo(() => ({ ...pair, expiry }), [pair, expiry]);

  useEffect(() => {
    let active = true;
    setExpiries(undefined);
    setExpiry(undefined);
    getDueDates(search.baseInstrumentId).then((choices) => {
      if (!active) return;
      setExpiries(choices);
      setExpiry(choices[0]?.id.toString());
    }).catch((error) => {
      if (!active) return;
      packetErrorHandler(error);
      setExpiries([]);
    });
    return () => { active = false; };
  }, [search.baseInstrumentId]);

  useEffect(() => {
    mutationController.current = new AbortController();
    return () => mutationController.current.abort();
  }, []);

  const mutate = async (action: () => Promise<IDeltaSimulationPosition[]>): Promise<boolean> => {
    setSaving(true);
    let refreshing = false;
    try {
      const result = await action();
      if (mutationController.current.signal.aborted) return false;
      setSimulationCount(result.length);
      const nextRefresh = refresh + 1;
      const remaining = new Set<DeltaTableName>(["summary", "portfolio"]);
      if (expiries !== undefined) remaining.add("chain");
      pendingRefresh.current = { key: nextRefresh, remaining };
      refreshing = true;
      setRefresh(nextRefresh);
      return true;
    } catch (error) {
      if (!mutationController.current.signal.aborted) packetErrorHandler(error);
      return false;
    } finally {
      if (!refreshing && !mutationController.current.signal.aborted) setSaving(false);
    }
  };

  const onRefreshComplete = (table: DeltaTableName, key: number) => {
    const pending = pendingRefresh.current;
    if (!pending || pending.key !== key || mutationController.current.signal.aborted) return;
    pending.remaining.delete(table);
    if (pending.remaining.size === 0) {
      pendingRefresh.current = null;
      setSaving(false);
    }
  };

  const onFetchError = (error: unknown) => { packetErrorHandler(error); return true; };

  return <SimulationActionsContext.Provider value={{
    disabled: saving,
    onAdd: (instrumentId, volume) => mutate(() => saveDeltaSimulation({ ...pair, instrumentId, volume }, { signal: mutationController.current.signal })),
    onEdit: (row, volume) => mutate(() => saveDeltaSimulation({ ...pair, id: row.simulationId!, instrumentId: row.instrumentId, volume }, { signal: mutationController.current.signal })),
    onDelete: (id) => mutate(() => deleteDeltaSimulation({ ...pair, id }, { signal: mutationController.current.signal })),
  }}><div className="flex flex-col gap-4 w-full min-w-0">
    {saving && <FullScreenLoading />}
    <div className="w-[540px] max-w-full"><TableTemplate TableUI={SummaryTableUI} getDataTable={getDeltaSummaryTable}
      searchParameters={pair} pagesize={2} silentRefreshKey={refresh} refreshTableTime={30000} onFetchError={onFetchError}
      onFetchComplete={() => onRefreshComplete("summary", refresh)}
      onResponse={(response) => {
        setHasMissingCalculations(response.report.some((row) => row.delta === null || row.gamma === null));
        setSimulationCount(response.report.reduce((count, row) => count + row.simulationCount, 0));
      }} /></div>
    {hasMissingCalculations && <p className="text-xs text-amber-700">برای برخی دارایی‌ها اطلاعات محاسبه موجود نیست؛ مجموع مربوط و تعداد خنثی‌سازی تا دریافت این اطلاعات نمایش داده نمی‌شوند.</p>}
    <div className="w-full overflow-x-auto">
      <div className="grid grid-cols-2 gap-4 min-w-[1480px]" dir="rtl">
        <section className="flex flex-col gap-3 min-w-0 border rounded p-3">
          <div className="flex items-center justify-end gap-3">
            <div className="w-fit"><Button text="ریست شبیه‌سازی" variant="outlined" disabled={saving || simulationCount === 0}
              onClick={() => { void mutate(() => resetDeltaSimulation(pair, { signal: mutationController.current.signal })); }} /></div>
          </div>
          <TableTemplate TableUI={PortfolioTableUI} getDataTable={getDeltaPortfolioTable} searchParameters={pair}
            silentRefreshKey={refresh} refreshTableTime={30000} withSort pagesize={ALL_ROWS_PAGE_SIZE} onFetchError={onFetchError}
            onFetchComplete={() => onRefreshComplete("portfolio", refresh)} />
        </section>
        <section className="flex flex-col gap-3 min-w-0 border rounded p-3">
          <SelectableButtons label="سررسید" name="expiry" hideLabel hideSelectAll options={expiries ?? []} value={expiry ?? ""} onChange={(_, value) => { if (value !== null) setExpiry(value.toString()); }} />
          {expiries !== undefined && <TableTemplate TableUI={OptionChainTableUI} getDataTable={getDeltaOptionChainTable} searchParameters={chainSearch}
            silentRefreshKey={refresh} refreshTableTime={30000} pagesize={ALL_ROWS_PAGE_SIZE} onFetchError={onFetchError}
            onFetchComplete={() => onRefreshComplete("chain", refresh)} />}
        </section>
      </div>
    </div>
  </div></SimulationActionsContext.Provider>;
};

export default DeltaPortfolio;
